import { FC, Fragment, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { GiftCouponSettlementSummaryItem } from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';

import {
  amountColorClass,
  formatSettlementAmount,
} from '../../utils/coupon-settlement.utils';
import { settlementDisplayTotal } from '../../utils/settlement.utils';
import {
  buildUnifiedMonthlyRows,
  collapseUnifiedRowsToMonth,
  computeUnifiedTotals,
  groupUnifiedRowsByMonth,
  UnifiedMonthlyRow,
  unifiedBrandOptions,
  unifiedYearOptions,
} from '../../utils/unified-settlement.utils';
import { exportUnifiedSettlementExcel } from '../../utils/unifiedSettlementExcel';
import { UnifiedDetailModal, UnifiedDetailTarget } from './UnifiedDetailModal';
import { PayoutDateSection } from './PayoutDateSection';
import { AmountCell, DetailButtons } from './UnifiedRowCells';

// ─────────────────────────────────────────────
// AgencyUnifiedMonthlySection
//
// 관리자·마리(GiftAgent)용 상조 정산 월별 합계.
// 상조가 여럿이라 기본 보기는 (정산월 × 상조) — "이번 달에 어느 상조에 얼마"가 바로 보이고,
// 월 소계·전체 합계 행으로 총액을 확인한다. '월 전체 합계' 보기로 접을 수도 있다.
// 금액 기준은 배송형·쿠폰형 각 탭과 동일(settlementDisplayTotal / summary.totalAmount).
// ─────────────────────────────────────────────

/** 이 화면 계열 카피 — 배송형/쿠폰형 탭과 동일하게 Draft를 '정산집계'로 부른다 */
const statusLabel = (s: GiftSettlementStatus) =>
  s === GiftSettlementStatus.Paid ? '지급완료' : '정산집계';

export interface AgencyUnifiedMonthlySectionProps {
  rebateItems: GiftRebateSettlement[] | undefined;
  couponItems: GiftCouponSettlementSummaryItem[] | undefined;
  loading: boolean;
  /** 마리(GiftAgent) 조회 뷰 — viaMari 건만, 경유 칼럼 숨김 */
  giftAgentView?: boolean;
  onOpenDelivery: () => void;
  onOpenCoupon: () => void;
}

type ViewMode = 'brand' | 'month';

const viaMariBadge = (viaMari: boolean | undefined) => {
  if (viaMari === undefined) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        viaMari ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {viaMari ? '마리 경유' : '가온 직접'}
    </span>
  );
};

const SummaryCard: FC<{
  label: string;
  amount: number;
  hint?: string;
  tone?: string;
}> = ({ label, amount, hint, tone = 'text-gray-800' }) => (
  <div className="rounded-lg bg-white p-4 text-center shadow">
    <p className={`text-2xl font-bold tabular-nums ${tone}`}>
      {formatSettlementAmount(amount)}
    </p>
    <p className="mt-1 text-sm text-gray-500">{label}</p>
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

export const AgencyUnifiedMonthlySection: FC<AgencyUnifiedMonthlySectionProps> = ({
  rebateItems,
  couponItems,
  loading,
  giftAgentView = false,
  onOpenDelivery,
  onOpenCoupon,
}) => {
  // 마리는 viaMari 상조만 — 서버 스코프의 중복 방어 (배송형/쿠폰형 탭과 동일 규칙)
  const scopedRebate = useMemo(() => {
    if (!rebateItems) return undefined;
    if (!giftAgentView) return rebateItems;
    return rebateItems.filter(
      (s) =>
        typeof s.bugoBrand === 'object' &&
        s.bugoBrand?.giftAccountConfig?.viaMari === true,
    );
  }, [rebateItems, giftAgentView]);

  const scopedCoupon = useMemo(() => {
    if (!couponItems) return undefined;
    return giftAgentView ? couponItems.filter((s) => s.viaMari === true) : couponItems;
  }, [couponItems, giftAgentView]);

  const allRows = useMemo(
    () =>
      buildUnifiedMonthlyRows(scopedRebate, scopedCoupon, {
        byBrand: true,
        // 관리자 화면 금액 기준 — 배송형 탭 목록(settlementDisplayTotal)과 동일하게 맞춘다
        rebateAmount: settlementDisplayTotal,
      }),
    [scopedRebate, scopedCoupon],
  );

  const years = useMemo(() => unifiedYearOptions(allRows), [allRows]);
  const brands = useMemo(() => unifiedBrandOptions(allRows), [allRows]);

  const [year, setYear] = useState<number | 'all'>('all');
  const [brandId, setBrandId] = useState<string>('all');
  const [view, setView] = useState<ViewMode>('brand');

  const filteredRows = useMemo(
    () =>
      allRows.filter(
        (r) =>
          (year === 'all' || r.year === year) &&
          (brandId === 'all' || r.brandId === brandId),
      ),
    [allRows, year, brandId],
  );

  const rows = useMemo(
    () => (view === 'month' ? collapseUnifiedRowsToMonth(filteredRows) : filteredRows),
    [filteredRows, view],
  );

  const totals = useMemo(() => computeUnifiedTotals(rows), [rows]);
  const monthGroups = useMemo(
    () => (view === 'brand' ? groupUnifiedRowsByMonth(rows) : []),
    [rows, view],
  );
  const allTotals = useMemo(() => computeUnifiedTotals(allRows), [allRows]);

  const yearLabel = year === 'all' ? '전체' : `${year}년`;
  const showViaMari = !giftAgentView;

  // 상세는 탭 이동 없이 이 자리에서 모달로 (문서 id로 재조회)
  const [detailTarget, setDetailTarget] = useState<UnifiedDetailTarget | null>(null);
  const openDoc = (kind: 'delivery' | 'coupon', docId: string) =>
    setDetailTarget({
      kind,
      docId,
      couponSummary:
        kind === 'coupon' ? couponItems?.find((s) => s._id === docId) : undefined,
    });
  // 정산월 / [상조] / [경유] / 배송형 / 쿠폰형 / 합계 / 상세
  const colCount =
    5 + (view === 'brand' ? 1 : 0) + (view === 'brand' && showViaMari ? 1 : 0);

  const renderRow = (r: UnifiedMonthlyRow, showMonth: boolean) => (
    <tr key={r.key} className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-800">
        {showMonth ? `${r.year}년 ${r.month}월` : ''}
      </td>
      {view === 'brand' && (
        <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
          {r.brandName ?? '-'}
        </td>
      )}
      {view === 'brand' && showViaMari && (
        <td className="px-3 py-3">{viaMariBadge(r.viaMari)}</td>
      )}
      <td className="whitespace-nowrap px-3 py-3 text-right">
        <AmountCell
          side={r.delivery}
          year={r.year}
          month={r.month}
          statusLabel={statusLabel}
          showBrand={view === 'month'}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right">
        <AmountCell
          side={r.coupon}
          year={r.year}
          month={r.month}
          statusLabel={statusLabel}
          showBrand={view === 'month'}
        />
      </td>
      <td
        className={`whitespace-nowrap px-3 py-3 text-right text-base font-bold tabular-nums ${amountColorClass(
          r.totalAmount,
        )}`}
      >
        {formatSettlementAmount(r.totalAmount)}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-center">
        <DetailButtons
          delivery={r.delivery}
          coupon={r.coupon}
          showBrand={view === 'month'}
          onOpenDoc={openDoc}
          onOpenTab={(kind) => (kind === 'delivery' ? onOpenDelivery() : onOpenCoupon())}
        />
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard
          label="미지급 (정산집계) 합계"
          amount={allTotals.draftAmount}
          hint="연도 필터와 무관한 전체 기준"
          tone="text-amber-700"
        />
        <SummaryCard
          label={`${yearLabel} 지급 완료`}
          amount={totals.paidAmount}
          tone="text-green-700"
        />
        <SummaryCard
          label={`${yearLabel} 총 정산금`}
          amount={totals.totalAmount}
          hint={`${new Set(filteredRows.map((r) => r.brandId)).size}개 상조 · ${
            new Set(filteredRows.map((r) => `${r.year}-${r.month}`)).size
          }개월`}
          tone="text-indigo-700"
        />
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setYear('all')}
            className={`rounded-full border px-3 py-1 text-sm ${
              year === 'all'
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`rounded-full border px-3 py-1 text-sm ${
                year === y
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {y}년
            </button>
          ))}
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="all">상조 전체</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-gray-300 text-sm">
            <button
              onClick={() => setView('brand')}
              className={`px-3 py-1 ${
                view === 'brand' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'
              }`}
            >
              상조별
            </button>
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1 ${
                view === 'month' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'
              }`}
            >
              월 전체 합계
            </button>
          </div>
          <button
            onClick={() =>
              exportUnifiedSettlementExcel(
                rows,
                totals,
                `상조정산합계_${year === 'all' ? '전체' : `${year}년`}.xlsx`,
                {
                  withBrand: view === 'brand',
                  withViaMari: view === 'brand' && showViaMari,
                  statusLabelFn: statusLabel,
                },
              )
            }
            disabled={rows.length === 0}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 표 */}
      {loading && !rebateItems && !couponItems ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left">정산월</th>
                {view === 'brand' && <th className="px-3 py-2.5 text-left">상조</th>}
                {view === 'brand' && showViaMari && (
                  <th className="px-3 py-2.5 text-left">경유</th>
                )}
                <th className="px-3 py-2.5 text-right">배송형 정산금</th>
                <th className="px-3 py-2.5 text-right">쿠폰형 정산금</th>
                <th className="px-3 py-2.5 text-right">합계</th>
                <th className="px-3 py-2.5 text-center">상세</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="px-3 py-10 text-center text-gray-400">
                    정산 내역이 없습니다.
                  </td>
                </tr>
              )}
              {view === 'month' && rows.map((r) => renderRow(r, true))}
              {view === 'brand' &&
                monthGroups.map((g) => (
                  <Fragment key={`${g.year}-${g.month}`}>
                    {g.rows.map((r, i) => renderRow(r, i === 0))}
                    {g.rows.length > 1 && (
                      <tr key={`${g.year}-${g.month}-sub`} className="bg-gray-50/70">
                        <td
                          colSpan={1 + 1 + (showViaMari ? 1 : 0)}
                          className="px-3 py-2 text-xs font-semibold text-gray-500"
                        >
                          {g.year}년 {g.month}월 소계 ({g.rows.length}개 상조)
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold tabular-nums text-gray-700">
                          {formatSettlementAmount(g.totals.deliveryAmount)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold tabular-nums text-gray-700">
                          {formatSettlementAmount(g.totals.couponAmount)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 text-right text-sm font-bold tabular-nums ${amountColorClass(
                            g.totals.totalAmount,
                          )}`}
                        >
                          {formatSettlementAmount(g.totals.totalAmount)}
                        </td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-100 font-semibold text-gray-800">
                <tr>
                  <td colSpan={colCount - 4} className="px-3 py-3">
                    전체 합계
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                      totals.deliveryAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.deliveryAmount)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                      totals.couponAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.couponAmount)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right text-base tabular-nums ${amountColorClass(
                      totals.totalAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.totalAmount)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-gray-500">
                    지급완료 {formatSettlementAmount(totals.paidAmount)} · 정산집계{' '}
                    {formatSettlementAmount(totals.draftAmount)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* 지급일 축 — "그 날 어느 상조에 총 얼마" (월별 표와 같은 필터 기준) */}
      <PayoutDateSection rows={rows} showBrand />

      <p className="text-xs text-gray-400">
        · 배송형·쿠폰형 정산서를 정산월 기준으로 합산한 값입니다(정산 문서는 종류별로 분리
        유지). 건별 상세는 <b>상세</b> 버튼으로 이 화면에서 바로 열리고,
        집행·수정·지급확정은 각 탭에서 진행하세요.
      </p>

      {/* 정산서 상세 — 탭 이동 없이 이 자리에서 (읽기 전용) */}
      <UnifiedDetailModal
        target={detailTarget}
        role={giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin}
        onClose={() => setDetailTarget(null)}
      />
    </div>
  );
};
